// The KPI list the dashboard is organised around: Daily / Weekly / Monthly, then
// groups, exactly as the business list was handed over. Each KPI carries an
// honest status, so the dashboard doubles as a progress report.
//
// status:
//   live       built and shown as a tile on the dashboard; `key` names the value
//              Dashboard.jsx supplies (see buildKpiValues)
//   elsewhere  already available on another page; `href` is where (no KPI is listed
//              this way right now — the page still supports it; add a helper back when one is)
//   next       the data is already recorded — only the report is still to build
//   data       not recorded yet — needs new tracking, a new field, or manual input
//
// Keep statuses truthful: a KPI is `live` only when it has a real number on the
// dashboard, and `data` whenever showing it would mean inventing the number.

export const KPI_STATUS = {
  live: { label: 'live', hint: 'Built — shown on the dashboard' },
  elsewhere: { label: 'on another page', hint: 'Available on another page — click to open it' },
  next: { label: 'next', hint: 'The data is recorded; the report is not built yet' },
  data: { label: 'needs data', hint: 'Not recorded yet — needs new tracking or input' },
};

const live = (label, key) => ({ label, key, status: 'live' });
const next = (label) => ({ label, status: 'next' });
const data = (label) => ({ label, status: 'data' });

export const KPI_SECTIONS = [
  {
    cadence: 'Daily',
    groups: [
      {
        title: 'Network',
        kpis: [
          live('Cafés onboarded', 'cafes_total'),
          live('Active cafés', 'cafes_active'),
          live('New cafés', 'cafes_new_today'),
          live('Cafés inactive 24h', 'cafes_inactive_24h'),
          live('Total screens', 'screens_total'),
          live('Screens offline', 'screens_offline'),
          data('Installations pending or with issues'),
        ],
      },
      {
        title: 'Gamer activity',
        kpis: [
          live('Active players', 'players_active_today'),
          live('New players', 'players_new_today'),
          live('Sessions per day', 'sessions_today'),
          live('Avg session duration', 'session_duration_today'),
          live('Total gaming hours', 'gaming_hours_today'),
          live('Repeat players', 'players_repeat'),
          live('New vs returning', 'players_new_vs_returning'),
        ],
      },
      {
        title: 'Revenue',
        kpis: [
          live('Revenue (this month)', 'revenue_mtd'),
          live('Recharge value', 'recharge_today'),
          live('Revenue per café', 'revenue_per_cafe_today'),
          live('Revenue per screen', 'rev_per_screen_today'),
          live('Revenue per session', 'rev_per_session_today'),
          live('F&B revenue', 'fnb_today'),
          live('NoLag revenue', 'nolag_today'),
          live('Collections pending', 'collections_pending'),
          live('Cash vs online', 'cash_online_today'),
        ],
      },
      {
        title: 'Product health',
        kpis: [
          live('System uptime', 'uptime_today'),
          live('Open support tickets', 'tickets_open'),
          live('Ticket closure rate', 'ticket_closure_rate'),
          live('Payment issues', 'payment_issues'),
          data('Critical bugs'),
          data('Tickets resolved (time to close)'),
          data('Sync / API failures'),
        ],
      },
    ],
  },
  {
    cadence: 'Weekly',
    groups: [
      {
        title: 'Growth',
        kpis: [
          live('New cafés', 'cafes_new_7d'),
          live('Active cafés', 'cafes_active'),
          live('Total screens', 'screens_total'),
          live('New players', 'players_new_7d'),
          live('Active players', 'players_active_7d'),
          next('New screens'),
          data('New cities'),
        ],
      },
      {
        title: 'Sales funnel',
        kpis: [
          data('Leads generated'),
          data('Leads contacted'),
          data('Demos / meetings'),
          data('Trials'),
          data('Installations'),
          data('Activated cafés'),
          data('Paid cafés'),
          data('Conversion %'),
          data('Salesperson performance'),
          data('City performance'),
          data('Avg sales cycle'),
          data('CAC'),
        ],
      },
      {
        title: 'Café performance',
        kpis: [
          live('Sessions', 'sessions_7d'),
          live('Gaming hours', 'gaming_hours_7d'),
          live('Repeat players', 'players_repeat'),
          live('Most-used games', 'most_used_games_week'),
          live('Screen utilization %', 'screen_utilization_week'),
          live('Revenue per screen', 'rev_per_screen_week'),
          live('Revenue per hour', 'revenue_per_hour_week'),
          live('Peak hours', 'peak_hours'),
          live('Support tickets', 'support_tickets'),
          data('Downtime'),
        ],
      },
      {
        title: 'Product and technology',
        kpis: [
          live('DAU / WAU', 'dau_wau'),
          live('Feature adoption', 'feature_adoption_week'),
          live('Dashboard usage', 'dashboard_usage_week'),
          live('Active café owners', 'owners_active'),
          live('Login frequency', 'owner_login_frequency'),
          live('Recharge / booking usage', 'recharge_booking_usage'),
          live('Uptime', 'uptime_7d'),
          data('Mobile app users'),
          data('Mobile app downloads'),
          data('Critical bugs'),
          data('Resolution time'),
        ],
      },
      {
        title: 'Customer success',
        kpis: [
          live('New cafés', 'cafes_new_7d'),
          live('Support tickets', 'support_tickets_by_cafe_week'),
          data('Churned cafés'),
          data('Churn %'),
          data('Churn reasons'),
          data('Complaints'),
          data('Resolution time'),
          data('NPS / satisfaction'),
          data('Feature requests'),
          data('Upsell opportunities'),
          data('Referrals'),
        ],
      },
      {
        title: 'Installation and operations',
        kpis: [
          live('Re-installations', 'reinstalls_week'),
          live('Post-install activation', 'post_install_activation_week'),
          data('Installation requests'),
          data('Installations completed'),
          data('Avg installation time'),
          data('Remote installation %'),
          data('Failed installations'),
          data('Installation cost'),
          data('Installation productivity'),
          data('Hardware issues'),
        ],
      },
      {
        title: 'City-wise',
        kpis: [
          data('Cafés by city'),
          data('Screens by city'),
          data('Active screens by city'),
          data('Players by city'),
          data('Revenue by city'),
          data('Leads by city'),
          data('Sales pipeline by city'),
          data('City growth'),
        ],
      },
    ],
  },
  {
    cadence: 'Monthly',
    groups: [
      {
        title: 'Revenue',
        kpis: [
          live('Revenue (this month)', 'revenue_mtd'),
          live('Month-on-month revenue', 'revenue_mom_month'),
          live('Revenue by café', 'revenue_by_cafe_month'),
          live('Revenue by PC vs F&B', 'pc_vs_fnb_month'),
          data('Revenue by city'),
          data('Churned MRR'),
        ],
      },
      {
        title: 'Costs',
        kpis: [
          data('Salaries'),
          data('Cloud / server'),
          data('Hardware'),
          data('Installation'),
          data('Sales and marketing'),
          data('Customer support'),
          data('Product development'),
          data('Other operating expenses'),
        ],
      },
      {
        title: 'Unit economics',
        kpis: [
          live('Revenue per café', 'revenue_per_cafe'),
          live('Revenue per screen', 'rev_per_screen_month'),
          data('Gross margin'),
          data('Contribution margin'),
          data('CAC'),
          data('Payback period'),
        ],
      },
      {
        title: 'Cash',
        kpis: [
          live('Collections', 'collections'),
          live('Receivables', 'receivables'),
          live('Payables', 'payables'),
          data('Opening cash'),
          data('Expenses'),
          data('Closing cash'),
          data('Monthly burn'),
          data('Runway'),
        ],
      },
    ],
  },
];

// How many KPIs are in each state, counting each (cadence, group, KPI) once —
// the same way the list was handed over, so the totals match it.
export function countKpiStatuses(sections = KPI_SECTIONS) {
  const totals = { live: 0, elsewhere: 0, next: 0, data: 0 };
  for (const section of sections) {
    for (const group of section.groups) {
      for (const kpi of group.kpis) totals[kpi.status] += 1;
    }
  }
  return totals;
}
